import { Link } from "react-router-dom";
import { Instagram, Facebook, Youtube, Twitter, Linkedin, Music2, Mail, Phone, MapPin, Globe, MessageCircle, Send } from "lucide-react";
import Logo from "./Logo";
import { useSiteLayout, isExternal } from "@/lib/siteLayout";

export const SOCIAL_ICONS = { instagram: Instagram, facebook: Facebook, youtube: Youtube, x: Twitter, twitter: Twitter,
  linkedin: Linkedin, tiktok: Music2, pinterest: Globe, threads: MessageCircle, telegram: Send, discord: MessageCircle, website: Globe };

export function SmartLink({ to, newTab, children, ...rest }) {
  if (isExternal(to)) return <a href={to} target={newTab || /^https?:/.test(to) ? "_blank" : undefined} rel="noopener noreferrer" {...rest}>{children}</a>;
  return <Link to={to} {...rest}>{children}</Link>;
}

function Contact({ c }) {
  if (!c?.email && !c?.phone && !c?.address) return null;
  return (
    <ul className="space-y-2 mt-5 text-sm" data-testid="footer-contact">
      {c.email && <li><a href={`mailto:${c.email}`} className="inline-flex items-center gap-2 text-foreground/80 hover:text-primary transition-colors" data-testid="footer-contact-email"><Mail className="h-4 w-4 text-primary" />{c.email}</a></li>}
      {c.phone && <li><a href={`tel:${c.phone}`} className="inline-flex items-center gap-2 text-foreground/80 hover:text-primary transition-colors" data-testid="footer-contact-phone"><Phone className="h-4 w-4 text-primary" />{c.phone}</a></li>}
      {c.address && <li className="flex items-start gap-2 text-muted-foreground" data-testid="footer-contact-address"><MapPin className="h-4 w-4 text-primary mt-0.5 shrink-0" />{c.address}</li>}
    </ul>
  );
}

export default function Footer() {
  const L = useSiteLayout();
  if (!L) return <footer className="border-t border-border mt-24 surface-2 h-64" />;
  const socials = (L.socials || []).filter((s) => s.url);
  return (
    <footer className="border-t border-border mt-24 surface-2" data-testid="site-footer">
      <div className="max-w-[1400px] mx-auto px-4 lg:px-8 py-14">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-8">
          <div className="col-span-2 md:col-span-1">
            <Logo />
            <p className="text-muted-foreground text-sm mt-4" data-testid="footer-tagline">{L.tagline}</p>
            <Contact c={L.contact} />
          </div>
          {(L.footer_columns || []).map((c, ci) => (
            <div key={ci}>
              <h4 className="track-label text-xs text-muted-foreground mb-4">{c.title}</h4>
              <ul className="space-y-3">
                {c.links.map((l, li) => (
                  <li key={li}>
                    <SmartLink to={l.url} newTab={l.new_tab} className="text-sm text-foreground/80 hover:text-primary transition-colors">{l.label}</SmartLink>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="mt-12 pt-6 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-muted-foreground" data-testid="footer-copyright">{L.copyright}</p>
          {socials.length > 0 && <div className="flex items-center gap-4 text-muted-foreground">
            {socials.map((s, i) => {
              const Icon = SOCIAL_ICONS[s.platform] || Globe;
              return <a key={i} href={s.url} target="_blank" rel="noopener noreferrer" aria-label={s.platform} className="hover:text-primary transition-colors" data-testid={`footer-social-${s.platform}`}><Icon className="h-5 w-5" /></a>;
            })}
          </div>}
        </div>
      </div>
    </footer>
  );
}
